import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import { tryOnApi } from '@wtf/shared/api/tryOn'
import {
    TryOnPhoto,
    TryOnPhotoUploadRequest,
    TryOnPhotoUploadResponse,
    GenerateTryOnRequest,
    GenerateTryOnResponse,
    TryOnGeneration,
} from '@wtf/shared'
import { platformSubscriptionKeys } from './usePlatformSubscription'
import { AnalyticsEvents } from '@/utils/analytics'
import { replaceImageDomain } from '@/utils/replaceImageDomain'

// Query keys
export const tryOnKeys = {
    all: ['tryOnPhotos'] as const,
    lists: () => [...tryOnKeys.all, 'list'] as const,
    list: () => [...tryOnKeys.lists()] as const,
    generations: (archived?: boolean) =>
        [...tryOnKeys.all, 'generations', archived] as const,
}

/**
 * Hook to get all try-on photos for the current user
 */
export function useGetTryOnPhotos() {
    return useQuery<TryOnPhoto[], Error>({
        queryKey: tryOnKeys.list(),
        queryFn: async () => {
            const photos = await tryOnApi.getTryOnPhotos()
            // Replace domain in all photo URLs
            return photos.map((photo) => ({
                ...photo,
                photoUrl: replaceImageDomain(photo.photoUrl),
            }))
        },
    })
}

/**
 * Hook to upload a new try-on photo
 */
export function useUploadTryOnPhoto() {
    const queryClient = useQueryClient()

    return useMutation<
        TryOnPhotoUploadResponse,
        Error,
        TryOnPhotoUploadRequest
    >({
        mutationFn: (variables) => tryOnApi.uploadTryOnPhoto(variables),
        onSuccess: () => {
            // Invalidate try-on photos list to refetch
            queryClient.invalidateQueries({ queryKey: tryOnKeys.list() })
        },
    })
}

/**
 * Hook to delete a try-on photo
 */
export function useDeleteTryOnPhoto() {
    const queryClient = useQueryClient()

    return useMutation<void, Error, string, { previousPhotos?: TryOnPhoto[] }>({
        mutationFn: (photoId) => tryOnApi.deleteTryOnPhoto(photoId),
        onMutate: async (photoId) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: tryOnKeys.list() })

            // Snapshot the previous value
            const previousPhotos = queryClient.getQueryData<TryOnPhoto[]>(
                tryOnKeys.list()
            )

            // Optimistically update to remove the photo
            if (previousPhotos) {
                queryClient.setQueryData<TryOnPhoto[]>(
                    tryOnKeys.list(),
                    previousPhotos.filter((photo) => photo.id !== photoId)
                )
            }

            return { previousPhotos }
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousPhotos) {
                queryClient.setQueryData(
                    tryOnKeys.list(),
                    context.previousPhotos
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success
            queryClient.invalidateQueries({ queryKey: tryOnKeys.list() })
        },
    })
}

/**
 * Hook to set a try-on photo as default
 */
export function useSetDefaultTryOnPhoto() {
    const queryClient = useQueryClient()

    return useMutation<
        TryOnPhoto,
        Error,
        string,
        { previousPhotos?: TryOnPhoto[] }
    >({
        mutationFn: (photoId) => tryOnApi.setDefaultTryOnPhoto(photoId),
        onMutate: async (photoId) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({ queryKey: tryOnKeys.list() })

            // Snapshot the previous value
            const previousPhotos = queryClient.getQueryData<TryOnPhoto[]>(
                tryOnKeys.list()
            )

            // Optimistically update to set the new default
            if (previousPhotos) {
                queryClient.setQueryData<TryOnPhoto[]>(
                    tryOnKeys.list(),
                    previousPhotos.map((photo) => ({
                        ...photo,
                        isDefault: photo.id === photoId,
                    }))
                )
            }

            return { previousPhotos }
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousPhotos) {
                queryClient.setQueryData(
                    tryOnKeys.list(),
                    context.previousPhotos
                )
            }
        },
        onSettled: () => {
            // Always refetch after error or success
            queryClient.invalidateQueries({ queryKey: tryOnKeys.list() })
        },
    })
}

/**
 * Hook to generate a virtual try-on image
 */
export function useGenerateTryOn() {
    const queryClient = useQueryClient()
    const posthog = usePostHog()

    return useMutation<GenerateTryOnResponse, Error, GenerateTryOnRequest, { startTime: number }>({
        mutationFn: (variables) => {
            // Track generation started
            posthog?.capture(AnalyticsEvents.TRY_ON_GENERATION_STARTED, {
                product_id: variables.productId,
                try_on_photo_id: variables.tryOnPhotoId,
                product_image_url: variables.productImageUrl,
            })
            return tryOnApi.generateTryOn(variables)
        },
        onMutate: () => {
            return { startTime: Date.now() }
        },
        onSuccess: (_data, variables, context) => {
            const duration = context?.startTime ? Math.round((Date.now() - context.startTime) / 1000) : undefined

            // Track successful generation
            posthog?.capture(AnalyticsEvents.TRY_ON_COMPLETED, {
                product_id: variables.productId,
                try_on_photo_id: variables.tryOnPhotoId,
                product_image_url: variables.productImageUrl,
                generation_time_seconds: duration,
            })

            // Invalidate platform subscription to update remaining count
            queryClient.invalidateQueries({
                queryKey: platformSubscriptionKeys.current(),
            })
        },
        onError: (error, variables, context) => {
            const duration = context?.startTime ? Math.round((Date.now() - context.startTime) / 1000) : undefined

            // Track failed generation
            posthog?.capture(AnalyticsEvents.TRY_ON_FAILED, {
                product_id: variables.productId,
                try_on_photo_id: variables.tryOnPhotoId,
                product_image_url: variables.productImageUrl,
                error_message: error.message,
                generation_time_seconds: duration,
            })
        },
    })
}

/**
 * Hook to get all try-on generations for the current user
 */
export function useGetTryOnGenerations(archived: boolean = false) {
    return useQuery<TryOnGeneration[], Error>({
        queryKey: tryOnKeys.generations(archived),
        queryFn: () => tryOnApi.getTryOnGenerations(archived),
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

/**
 * Hook to archive a try-on generation
 */
export function useArchiveTryOnGeneration() {
    const queryClient = useQueryClient()

    return useMutation<
        void,
        Error,
        string,
        { previousGenerations?: TryOnGeneration[] }
    >({
        mutationFn: (generationId) =>
            tryOnApi.archiveTryOnGeneration(generationId),
        onMutate: async (generationId) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({
                queryKey: tryOnKeys.generations(false),
            })

            // Snapshot the previous value
            const previousGenerations = queryClient.getQueryData<
                TryOnGeneration[]
            >(tryOnKeys.generations(false))

            // Optimistically update to remove from non-archived list
            if (previousGenerations) {
                queryClient.setQueryData<TryOnGeneration[]>(
                    tryOnKeys.generations(false),
                    previousGenerations.filter((gen) => gen.id !== generationId)
                )
            }

            return { previousGenerations }
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousGenerations) {
                queryClient.setQueryData(
                    tryOnKeys.generations(false),
                    context.previousGenerations
                )
            }
        },
        onSettled: () => {
            // Invalidate both archived and non-archived queries
            queryClient.invalidateQueries({
                queryKey: tryOnKeys.generations(false),
            })
            queryClient.invalidateQueries({
                queryKey: tryOnKeys.generations(true),
            })
        },
    })
}

/**
 * Hook to unarchive a try-on generation
 */
export function useUnarchiveTryOnGeneration() {
    const queryClient = useQueryClient()

    return useMutation<
        void,
        Error,
        string,
        { previousGenerations?: TryOnGeneration[] }
    >({
        mutationFn: (generationId) =>
            tryOnApi.unarchiveTryOnGeneration(generationId),
        onMutate: async (generationId) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({
                queryKey: tryOnKeys.generations(true),
            })

            // Snapshot the previous value
            const previousGenerations = queryClient.getQueryData<
                TryOnGeneration[]
            >(tryOnKeys.generations(true))

            // Optimistically update to remove from archived list
            if (previousGenerations) {
                queryClient.setQueryData<TryOnGeneration[]>(
                    tryOnKeys.generations(true),
                    previousGenerations.filter((gen) => gen.id !== generationId)
                )
            }

            return { previousGenerations }
        },
        onError: (_err, _variables, context) => {
            // Rollback on error
            if (context?.previousGenerations) {
                queryClient.setQueryData(
                    tryOnKeys.generations(true),
                    context.previousGenerations
                )
            }
        },
        onSettled: () => {
            // Invalidate both archived and non-archived queries
            queryClient.invalidateQueries({
                queryKey: tryOnKeys.generations(false),
            })
            queryClient.invalidateQueries({
                queryKey: tryOnKeys.generations(true),
            })
        },
    })
}
